import allowed from '@/core/constants/allowed-roles.constant'

const routes = [
  {
    name: 'incident-investigation-public',
    path: '/incident-investigation/public/:id',
    meta: {
      requiresAuth: false,
      layout: 'public',
      module: 'incident-management',
    },
    component: () => import('@/modules/incident-management/containers/incident-investigation-public/incident-investigation-public.container.vue'),
    beforeEnter: async (to, from, next) => {
      const { usePublicAuth } = await import('@/modules/incident-management/composables/use-public-auth.composable')
      const { exchangeKey, initializeFromStorage, isAuthenticated } = usePublicAuth()

      // Try to initialize from storage first
      initializeFromStorage()

      const key = to.query.key

      if (!key && !isAuthenticated.value) {
        // No key and not authenticated, maybe show 404 or error
        next() // Or redirect to an error page
        return
      }

      if (key && !isAuthenticated.value) {
        try {
          await exchangeKey(key)
          next()
        }
        catch (error) {
          console.error('Failed to exchange key:', error)
          next() // Or redirect to error
        }
      }
      else {
        next()
      }
    },
  },
  {
    name: 'safety-management',
    path: '/safety-management',
    meta: {
      requiresAuth: true,
      layout: 'home',
      module: 'incident-management',
      breadcrumb: {
        label: 'incident-management.breadcrumbs.safety-management',
        route: 'safety-management',
      },
    },
    children: [
      {
        name: 'incident-management',
        path: 'incident-management',
        props: true,
        meta: {
          allowedRoutes: [...allowed.incident_management.roles],
          breadcrumb: {
            label: 'incident-management.breadcrumbs.incident-management',
            route: 'incident-management-home',
          },
        },
        children: [
          {
            name: 'incident-management-home',
            path: '',
            component: () =>
              import(
                '@/modules/incident-management/components/incident-management-home/incident-management-home.vue'
              ),
          },
          {
            name: 'incident-report',
            path: 'incident-report',
            meta: {
              breadcrumb: {
                label: 'incident-management.breadcrumbs.incident-report',
                route: 'incident-report',
                parent: 'incident-management',
              },
            },
            component: () =>
              import(
                '@/modules/incident-management/containers/incident-report/incident-report.container.vue'
              ),
          },
          {
            name: 'Edit incident report',
            path: 'incident-report/:id',
            component: () =>
              import(
                '@/modules/incident-management/containers/incident-report/incident-report.container.vue'
              ),
          },
          {
            name: 'Incident investigation',
            path: 'incident-investigation/:id',
            component: () =>
              import(
                '@/modules/incident-management/containers/incident-investigation/incident-investigation.container.vue'
              ),
          },
          {
            name: 'Incident investigation full',
            path: 'incident-investigation-full/:id',
            component: () =>
              import(
                '@/modules/incident-management/containers/incident-investigation-full/incident-investigation-full.container.vue'
              ),
          },
          {
            name: 'Incident approval',
            path: 'incident-approval/:id',
            component: () =>
              import(
                '@/modules/incident-management/containers/incident-approval/incident-approval.container.vue'
              ),
          },
          {
            name: 'Accident victim',
            path: ':id/accident-victim',
            component: () =>
              import(
                '@/modules/incident-management/containers/accident-victim/accident-victim.container.vue'
              ),
            meta: {
              breadcrumb: {
                label: 'incident-management.accident-victim.title',
                parent: 'incident-management',
              },
            },
            children: [
              {
                name: 'Injured person data',
                path: 'injured-person-data/:injuredId?',
                component: () =>
                  import(
                    '@/modules/incident-management/containers/injured-person-data/injured-person-data.container.vue'
                  ),
                meta: {
                  breadcrumb: {
                    label:
                      'incident-management.injured-person-form.title',
                    parent: 'Accident victim',
                  },
                },
              },
            ],
          },
          {
            name: 'Incident Management Data and facts',
            path: ':incidentId/data-and-facts',
            props: true,
            component: () =>
              import(
                '@modules/incident-management/apps/data-and-facts/containers/data-and-facts.container.vue'
              ),
            meta: {
              breadcrumb: {
                label:
                  'incident-management.data-and-facts.breadcrumbs.data-and-facts',
                parent: 'incident-management-home',
              },
            },
          },
          {
            name: 'Incident Management Investigation',
            path: ':incidentId/investigation',
            props: true,
            meta: {
              breadcrumb: {
                label: 'incident-management.breadcrumbs.investigation',
                parent: 'incident-management-home',
              },
            },
            children: [
              {
                name: 'Incident Management Investigation Ishikawa Diagram',
                path: 'ishikawa-diagram',
                props: true,
                component: () =>
                  import(
                    '@modules/incident-management/apps/ishikawa-diagram/containers/ishikawa-diagram.container.vue'
                  ),
                meta: {
                  breadcrumb: {
                    label:
                      'incident-management.ishikawa-diagram.breadcrumbs.label',
                    parent: 'Incident Management Investigation',
                  },
                },
              },
              {
                name: 'Incident Management Investigation Five Whys',
                path: 'five-whys',
                props: true,
                component: () =>
                  import(
                    '@modules/incident-management/apps/five-whys/containers/five-whys.container.vue'
                  ),
                meta: {
                  breadcrumb: {
                    label: 'incident-management.five-whys.breadcrumbs.label',
                    parent: 'Incident Management Investigation',
                  },
                },
              },
            ],
          },
        ],
      },
      {
        name: 'incident-settings',
        path: 'incident-management/settings',
        props: true,
        meta: {
          allowedRoles: [...allowed.incident_management.settings],
          breadcrumb: {
            label: 'incident-management.breadcrumbs.incident-management',
            route: 'incident-settings-home',
          },
        },
        children: [
          {
            name: 'incident-settings-home',
            path: '',
            meta: {
              breadcrumb: {
                label: 'incident-management.breadcrumbs.incident-settings',
                route: 'incident-settings-home',
              },
            },
            component: () =>
              import(
                '@/modules/incident-management/components/incident-settings/incident-settings.vue'
              ),
          },
          {
            name: 'incident-notifications',
            path: 'notifications',
            meta: {
              breadcrumb: {
                label: 'incident-management.breadcrumbs.incident-notifications',
                route: 'incident-notifications',
              },
            },
            component: () =>
              import(
                '@/modules/incident-management/components/email-administration/email-administration.vue'
              ),
          },
          {
            name: 'incident-investigators',
            path: 'investigators',
            meta: {
              breadcrumb: {
                label: 'incident-management.breadcrumbs.incident-investigators',
                route: 'incident-investigators',
              },
            },
            component: () =>
              import(
                '@/modules/incident-management/components/parameterization-of-investigators/parameterization-of-investigators.vue'
              ),
          },
          {
            name: 'incident-signatories',
            path: 'signatories',
            meta: {
              breadcrumb: {
                label: 'incident-management.breadcrumbs.incident-signatories',
                route: 'incident-signatories',
              },
            },
            component: () =>
              import(
                '@/modules/incident-management/components/signatories/signatories.vue'
              ),
          },
        ],
      },
    ],
  },
]

export default routes
